import React, { useState } from 'react'
import classNames from 'classnames'
import ReactDOM from 'react-dom'

import Form from '../forms/Form';
import TextField from '../forms/TextField';
import { minLength, validEmail, required, validate } from '../forms/validations';
import SignupFormProgressIndicator from './SignupFormProgressIndicator';
import StripeCardField from './StripeCardField';
import { getFunnelContent, getPlanByPriceId } from '../../shared/plans';
import posthogClient from '../../shared/posthogClient';
import { enroll, createUserAndSubscription, completeRegistration } from './backend';
import useReCAPTCHA from "./useReCAPTCHA";
import { getStripe, buildStripeError } from '../stripe';
import { dispatchEvent } from '../../shared/events';
import WithSalesBenefits from './WithSalesBenefits';
import wordtrackerLogo from '../../images/wordtracker-logo-black.png';

const stageNames = ["details", "billing", "done"];

export default function CreateUserAndSubscribe({user, priceId, funnel, onClose}) {

  const initialFormValues = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
  }

  const salesText = getFunnelContent(priceId, funnel);
  const plan = getPlanByPriceId(priceId);
  const price = plan.unit_amount_in_cents / 100;

  const [ CardElement, SetCardElement ] = useState(null);
  const [ Progress, SetProgress ] = useState(1);
  const [ ActiveStage, SetActiveStage ] = useState(1);
  const [ Processing, SetProcessing ] = useState(false);
  const [ Success, SetSuccess ] = useState(false);
  const [ FormData, SetFormData ] = useState(initialFormValues);
  const [ Errors, SetErrors ] = useState({});
  const [ CachedClientSecret, SetCachedClientSecret ] = useState(null);
  const [ CachedRegistrationToken, SetCachedRegistrationToken ] = useState(null);

  const {
    ReCAPTCHAComponent,
    getReCAPTCHAToken,
  } = useReCAPTCHA(__WORDTRACKER_INVISIBLE_RECAPTCHA_SITE_KEY)

  const goToStage = (stage, advanceProgress = false) => {
    SetProcessing(false);
    const lastStageNum = stageNames.length;
    if (stage === ActiveStage) return false;
    if (stage > lastStageNum || stage < 1) return false;
    if (ActiveStage === lastStageNum) return false;
    if (!advanceProgress && stage > Progress) return false;
    if (stage > Progress) SetProgress(stage);
    SetActiveStage(stage);
  };

  const handleChangeField = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    SetFormData({
      ...FormData,
      [e.target.name]: value
    });
    if (Errors[e.target.name]) {
      SetErrors({ ...Errors, [e.target.name]: undefined });
    }
  }

  const handleBackendError = errors => {
    SetErrors(errors);
    SetProcessing(false);
    if ((errors.email || errors.password) && ActiveStage != 1) {
      goToStage(1);
    }
  }

  const handleErrorStateChange = (e, error) => {
    if (error) console.error(error);
    const fieldName = [e.target.name];
    let _errors = { ...Errors, [fieldName]: error };
    _errors = Object.fromEntries(Object.entries(_errors).filter(([key, value]) => value !== null));
    SetErrors(_errors);
  }

  const handleBackToContinueAccess = (e) => {
    e.preventDefault();
    onClose();
    setTimeout(() => dispatchEvent('requestsExhausted'), 200);
  }

  const detailsComplete = () => {
    posthogClient.capture('signup_modal_billing');
    SetProcessing(false);
    return goToStage(2, true);
  }

  const subscribeComplete = (createdUser) => {
    SetProcessing(false);
    SetSuccess(true);
    goToStage(3, true);
    posthogClient.capture('signup_modal_complete');
    const loginUrl = `${__ZUUL_URL}/consumer_search?user_email=${encodeURIComponent(createdUser.email)}&user_token=${createdUser.token}`;
    setTimeout(() => window.location.href = loginUrl, 1200);
  }

  const handleSubmitDetails = async () => {
    const emailError = validate(FormData.email, [required(), validEmail()]);
    const passwordError = validate(FormData.password, [minLength(6)]);
    if (emailError || passwordError) {
      return SetErrors({
        ...(emailError && { email: emailError }),
        ...(passwordError && { password: passwordError }),
      });
    }

    SetErrors({});
    SetProcessing(true);
    const token = await getReCAPTCHAToken();
    try {
      await enroll(token, {email: FormData.email});
      return detailsComplete();
    } catch(error) {
      return handleBackendError(error);
    }
  }

  const confirmCardPayment = async (client_secret) => {
    const stripe = await getStripe();
    const cardDetails = { payment_method: { card: CardElement, billing_details: { name: `${FormData.first_name} ${FormData.last_name}` } } }
    return await stripe.confirmCardPayment(client_secret, cardDetails);
  }

  const handleSubmitCard = async () => {
    SetErrors({});
    SetProcessing(true);

    try {
      let client_secret = CachedClientSecret;
      let registration_token = CachedRegistrationToken;

      if (!client_secret || !registration_token) {
        const token = await getReCAPTCHAToken();
        const {user: _user, client_secret: _client_secret} = await createUserAndSubscription(token, { ...FormData, plan_code: priceId });
        client_secret = _client_secret;
        registration_token = _user.registration_token;

        SetCachedClientSecret(client_secret);
        SetCachedRegistrationToken(registration_token);
      }

      const stripeResponse = await confirmCardPayment(client_secret);
      const stripeError = buildStripeError(stripeResponse);
      if (stripeError) return handleBackendError({card_number: stripeError});

      const { user: createdUser } = await completeRegistration({ ...FormData, registration_token, plan_code: priceId, payment_method_id: stripeResponse.paymentIntent.payment_method });
      return subscribeComplete(createdUser);
    } catch(error) {
      return handleBackendError(error);
    }
  }

  const isSubmitDisabled = Processing;
  const currentStage = stageNames[ActiveStage - 1];

  const renderDetailsForm = () => {
    return (
      <div className='sales-modal-stage details fade-in' style={{display: currentStage === 'details' ? 'flex' : 'none'}}>

        <div className='sales-modal-stage-header'>
          <img className='sales-modal-logo' src={wordtrackerLogo} alt='Wordtracker' />
          <h2>Get everything Wordtracker has to offer for a month for ${price}</h2>
        </div>

        <div className="sales-modal-field">
          <TextField
            name='email'
            type='text'
            label='Email address'
            klassname='email'
            initialValue={FormData.email}
            onErrorStateChange={handleErrorStateChange}
            onChange={handleChangeField}
            error={Errors.email === 'already taken' ? <>already taken. <a href={`${__ZUUL_URL}/login`}>Log in here.</a></> : Errors.email}
            validations={[ required(), validEmail() ]}
            placeholder='name@example.com'
          />
        </div>

        <div className="sales-modal-field">
          <TextField
            name='password'
            type='password'
            label='Password'
            klassname='password'
            initialValue={FormData.password}
            onErrorStateChange={handleErrorStateChange}
            onChange={handleChangeField}
            validations={[ minLength(6) ]}
            error={Errors.password}
          />
        </div>

        <button
          className='sales-modal-button'
          type='button'
          disabled={isSubmitDisabled}
          onClick={handleSubmitDetails}
        >{Processing ? 'Processing...' : <>Unlock full access for ${price} <i className='fa-solid fa-arrow-right'></i></>}</button>

        {salesText.buttonDetailsHint && <p className='sales-modal-hint button-details-hint'>{salesText.buttonDetailsHint}</p>}

        <p className='sales-modal-plans-link'>
          <a href={`${__ZUUL_URL}/sign-up`} target='_blank' id='modal-see-all-plans-link'><i className='fa-solid fa-layer-group'></i> See all plans and pricing</a>
        </p>

      </div>
    )
  }

  const renderBillingForm = () => {
    const isBillingDisabled = !FormData.first_name || !FormData.last_name;

    return (
      <div className='sales-modal-stage billing fade-in' style={{display: currentStage === 'billing' ? 'flex' : 'none'}}>

        <div className='sales-modal-stage-header'>
          <img className='sales-modal-logo' src={wordtrackerLogo} alt='Wordtracker' />
          <h2>Get everything Wordtracker has to offer for a month for ${price}</h2>
        </div>

        <div className='sales-modal-field'>
          <label className={`number ${classNames({"has_error": Errors.card_number})}`}>
            Card details&nbsp;
            { Errors.card_number && <span className='error'>{Errors.card_number}</span> }
            <StripeCardField
              cardElement={CardElement}
              onCardElement={SetCardElement}/>
          </label>
        </div>

        <div className='sales-modal-field sales-modal-dual-field'>
          <TextField
            name='first_name'
            type='text'
            label='First name'
            klassname='first-name'
            initialValue={FormData.first_name}
            onErrorStateChange={handleErrorStateChange}
            onChange={handleChangeField}
            error={Errors.first_name}
            validations={[ required() ]}
          />
          <TextField
            name='last_name'
            type='text'
            label='Last name'
            klassname='last-name'
            initialValue={FormData.last_name}
            onErrorStateChange={handleErrorStateChange}
            onChange={handleChangeField}
            error={Errors.last_name}
            validations={[ required() ]}
          />
        </div>

        <button
          onClick={handleSubmitCard}
          className={`sales-modal-button ${classNames({success: Success})}`}
          disabled={Processing || Success || isBillingDisabled}
        >{ Success ? "Success!" : Processing ? "Processing..." : <><i className='fa-solid fa-lock'></i> {salesText.buttonBilling}</> }</button>

        {salesText.buttonHint && <p className='sales-modal-hint button-details-hint'>{salesText.buttonHint}</p>}

        <p className='sales-modal-plans-link'>
          <a href={`${__ZUUL_URL}/sign-up`} target='_blank' id='modal-see-all-plans-link'><i className='fa-solid fa-layer-group'></i> See all plans and pricing</a>
        </p>

      </div>
    )
  }

  const renderDone = () => (
    <div className='sales-modal-stage sales-modal-done fade-in' style={{display: currentStage === 'done' ? 'flex' : 'none'}}>
      <div className='big-tick'></div>
      <h2>Sign-up complete.</h2>
      {salesText.doneHtml}
    </div>
  )

  return (
    <div className='sales-modal-container'>
      {ReactDOM.createPortal(ReCAPTCHAComponent, document.body)}

      {currentStage !== 'done' && (
        <div className='sales-modal-ribbon-clip'>
          <div className='sales-modal-ribbon'>
            <span><strong>${price}</strong> only</span>
          </div>
        </div>
      )}

      <WithSalesBenefits plan={plan} hidePanel={currentStage === 'done'}>
        <div className='sales-modal-left-inner'>

          <Form name='stripeForm' id='stripe-form-signup'>
            { renderDetailsForm() }
            { Progress >= 2 && renderBillingForm() }
            { renderDone() }
            <input type="hidden" name="stripe-token" />
          </Form>

          {currentStage !== 'done' && (
            <a className='create-user-subscribe-try-more' href='#' onClick={handleBackToContinueAccess}>
              <i className='fa-solid fa-arrow-left'></i> Can I try the tool out some more instead?
            </a>
          )}

        </div>

        {currentStage !== 'done' && (
          <div className='sales-modal-progress'>
            <SignupFormProgressIndicator
              onClickNode={goToStage}
              progress={Progress}
              activeStage={ActiveStage}
            />
          </div>
        )}
      </WithSalesBenefits>
    </div>
  )
}
